import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import RRDJoinCampaignForm from "./RRDJoinCampaignForm";
import { rrdDirectMailCampaignHelper } from "../utils/rrdDirectMailHelper";
import { rrdCampaignService } from "../services/rrdCampaignService";
import type { RRDDraftBulkRequest } from "../services/rrdCampaignService";
import { logger } from "@/helpers/loggerHelper";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { HTTP_STATUS } from "@/config/constants";
import { ROUTES, generatePath } from "@/helpers/routes/routePaths";

type RRDRecord = Record<string, any>;

type RRDJoinCampaignProps = {
  campaign?: RRDRecord;
};

function RRDJoinCampaign({ campaign }: RRDJoinCampaignProps) {
  const navigate = useNavigate();
  const loader = useContext(LoaderContext) as (_loading: boolean) => void;
  const { loggedInUser } = useContext(GlobalContext) as RRDRecord;

  const [campaignDrafttedData, setCampaignDrafttedData] =
    useState<RRDRecord | null>(null);

  const getInitialValues = useMemo(() => {
    if (!campaign) return {};

    return rrdDirectMailCampaignHelper.getInitialValues(
      campaign,
      campaignDrafttedData
    );
  }, [campaign, campaignDrafttedData]);

  const fetchDraftData = useCallback(
    async (campaignID: any) => {
      try {
        loader(true);
        const data = {
          campaignId: campaignID,
          fkParticipantId: loggedInUser?.participantData?.id,
        };
        const res = await rrdCampaignService.getDraftRRDCampaign(data);
        if (res.status === HTTP_STATUS.OK) {
          const data = res.data;
          if (String(data?.campaignId) === String(campaignID)) {
            setCampaignDrafttedData(data);
          }
        }
      } catch (err) {
        logger.error("Fetch draftted data error", err);
      } finally {
        loader(false);
      }
    },
    [loader, loggedInUser?.participantData?.id]
  );

  const saveAsDraft = useCallback(
    async (data: RRDDraftBulkRequest) => {
      try {
        loader(true);
        const res = await rrdCampaignService.draftRRDCampaign(data);
        if (res.status === HTTP_STATUS.OK) {
          if (data?.is_draft) {
            navigate(ROUTES.LAUNCH_DIRECT_MAIL, { replace: true });
          } else {
            navigate(
              generatePath.rrdJoinCampaignConfirm(campaign?.campaignId),
              {
                replace: true,
                state: {
                  campaignId: campaign?.campaignId,
                },
              }
            );
          }
        }
      } catch (err) {
        logger.error("RRD Draft Error:", err);
      } finally {
        loader(false);
      }
    },
    [loader, campaign?.campaignId, navigate]
  );

  useEffect(() => {
    if (campaign?.campaignId) {
      fetchDraftData(campaign?.campaignId);
    }
  }, [fetchDraftData, campaign?.campaignId]);

  return (
    <div>
      <RRDJoinCampaignForm
        campaign={campaign}
        initialValues={getInitialValues}
        onSubmit={saveAsDraft}
        drafttedOrderIds={campaignDrafttedData?.orderIds}
      />
    </div>
  );
}

export default RRDJoinCampaign;
